import {
  InfoCircleOutlined,
  DeleteOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  Button,
  Select,
  Skeleton,
  Table,
  Tooltip,
  Modal,
  Input,
  Popover,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { classNames } from 'src/utilities';
import {
  getOrgSetting,
  putOrgSetting,
  deleteUser,
} from 'src/modules/SuperAdmin/store/actions/organizationsetting.action';
import { UpgradeModal } from 'src/modules/ambient_solo/components/Payments/UpgradeModal';
import { UpgradeButton } from 'src/modules/ambient_solo/components/Payments/UpgradeButton';
import { useIsFreeUser } from 'src/hooks/common';

const DataArchivalSetting = ({ isDataArchiveDeletingUserAllowed }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [activePopover, setActivePopover] = useState(null);
  const { currentPractitioner, currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const { orgSetting } = useSelector((state) => state?.orgSettingState);
  const [archivalDays, setArchivalDays] = useState('21');
  const isFreeUser = useIsFreeUser();

  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type == 'pro'
  );

  const isAuraTeams = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'teams'
  );

  const handleUpgradeModal = () => {
    setIsUpgradeModalVisible(true);
  };

  const handleShowUpgradeTooltip = (element) => {
    if (isFreeUser) {
      setActivePopover(element);
    }
  };

  const handleHideUpgradeTooltip = () => {
    setActivePopover(null);
  };

  const handleArchivalDaysChange = (value) => {
    if (!isFreeUser) {
      setArchivalDays(value);
    }
  };

  useEffect(() => {
    if (currentPractitioner?.org_uuid) {
      dispatch(getOrgSetting('archival_settings'));
    }
  }, [currentPractitioner, dispatch]);

  const handleSubmit = async () => {
    if (isFreeUser) {
      handleShowUpgradeTooltip('select');
      return;
    }

    const value = {
      aura_archival_enabled: true,
      aura_archival_days: archivalDays,
      vca_archival_enabled: true,
      vca_archival_days: archivalDays,
    };
    dispatch(putOrgSetting('archival_settings', value));
  };

  useEffect(() => {
    if (orgSetting && !isFreeUser) {
      setArchivalDays(orgSetting?.settings?.vca_archival_days);
    }
  }, [orgSetting, isFreeUser]);

  const handleDelete = async () => {
    if (deleteConfirmText === 'Delete User') {
      setIsLoading(true);
      try {
        if (
          currentPractitioner?.org_uuid &&
          currentPractitioner?.practitioner_id
        ) {
          await dispatch(
            deleteUser(
              currentPractitioner?.org_uuid,
              currentPractitioner?.practitioner_id
            )
          );
          window.location.reload();
        }
        setShowDeleteModal(false);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="flex-1 pt-4">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-lg font-bold font-sans pl-1 mb-2 flex items-center gap-2">
              Select Retention Period
              {isFreeUser && (
                <Tooltip title="Upgrade to Pro to customize retention period">
                  <LockOutlined className="text-gray-400" />
                </Tooltip>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Popover
                content={
                  <div
                    onMouseEnter={() => handleShowUpgradeTooltip('select')}
                    onMouseLeave={handleHideUpgradeTooltip}
                  >
                    <UpgradeButton
                      text="Upgrade to access this feature"
                      analyticsEvent="Clicked Data Retention Upgrade"
                    />
                  </div>
                }
                open={isFreeUser && activePopover === 'select'}
                trigger="hover"
                placement="rightTop"
                mouseLeaveDelay={0.3}
                overlayInnerStyle={{ padding: '0px' }}
                align={{
                  offset: [0, 0],
                }}
                onOpenChange={(visible) => {
                  if (!visible) {
                    handleHideUpgradeTooltip();
                  }
                }}
              >
                <div
                  onMouseEnter={() => handleShowUpgradeTooltip('select')}
                  onMouseLeave={handleHideUpgradeTooltip}
                >
                  <Select
                    value={archivalDays}
                    onChange={handleArchivalDaysChange}
                    style={{
                      width: '300px',
                      borderColor: '#d9d9d9',
                      borderRadius: '8px',
                    }}
                    size="large"
                    disabled={isFreeUser}
                    className="border border-gray-200 hover:border-blue-400 focus:border-blue-400 rounded-md"
                    options={[
                      { value: '21', label: '21 Days' },
                      { value: '30', label: '30 Days' },
                      { value: '60', label: '60 Days' },
                      { value: '90', label: '90 Days' },
                    ]}
                    suffixIcon={
                      isFreeUser && <LockOutlined className="text-gray-400" />
                    }
                  />
                </div>
              </Popover>
              <div>
                <Button
                  type="primary"
                  onClick={
                    isFreeUser
                      ? () => handleShowUpgradeTooltip('select')
                      : handleSubmit
                  }
                  loading={isLoading}
                  size="large"
                  style={{ width: '300px' }}
                  className="mt-2"
                  icon={isFreeUser && <LockOutlined />}
                >
                  {!isFreeUser ? 'Submit' : 'Upgrade to Pro'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDataArchiveDeletingUserAllowed && (
        <div className="bg-white rounded-lg shadow-sm p-6 mt-4 border-2 border-red-200">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div className="text-lg font-bold font-sans pl-1 mb-2 text-red-600">
                Danger Zone
              </div>
              <div className="text-sm text-gray-500 pl-1 mb-4">
                Once you delete a user, there is no going back. Please be
                certain.
              </div>
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() => setShowDeleteModal(true)}
                size="large"
                style={{ width: 'fit-content', borderColor: '#F5222D' }}
              >
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Delete User"
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteConfirmText('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={isLoading}
            disabled={deleteConfirmText !== 'Delete User'}
            onClick={handleDelete}
          >
            Delete
          </Button>,
        ]}
      >
        <p>
          This action cannot be undone. Please type{' '}
          <span className="text-black-600 font-semibold">"Delete User"</span> to
          confirm.
        </p>
        <Input
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder='Type "Delete User"'
          className="mt-4"
        />
      </Modal>

      <UpgradeModal
        isVisible={isUpgradeModalVisible}
        setIsVisible={setIsUpgradeModalVisible}
      />
    </div>
  );
};

export default DataArchivalSetting;
